// imperial-box-ui — the Box primitive.
//
// Ported from lcars-dom/box.js (1432 lines). This is the TEXT-INDEPENDENT subset:
// flex layout (row/column, gap, padding, grow/shrink, alignment), background +
// border meshes (borderWidths/borderColors/borderRadii), overflow + clip-uniform
// plumbing, the render surface, child hosting (add/remove), and layout().
//
// DROPPED from the source (now imperial-text-base's job): setText/updateTextMesh/
// setFont, the createText/measureText import and all _textMesh/_textResult/text-
// scroll code, and text measurement inside layout()/measureIntrinsic(). A box is
// text-free; a Text drops in as an ordinary child Object3D.
//
// AUTO-SIZE: a box with no explicit width/height sizes to its children. A non-Box
// child is measured generically — honor an optional child.getSize() if present,
// else fall back to new THREE.Box3().setFromObject(child).
//
// CAPABILITIES ARE INJECTED: new Box({ clickable, scrollable }) where clickable/
// scrollable are factories imported from their own modules. Omitting one keeps its
// code out of the module graph (tree-shaking by not-importing).
//
// Imports ONLY three (+ a local createClipMaterial). No text engine, no text-base,
// no capability imports.
import * as THREE from 'three';

const vertexShader = `
    varying vec2 vPos;
    varying vec2 vUv;
    void main() {
        vPos = position.xy;
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const fragmentShader = `
    varying vec2 vPos;
    varying vec2 vUv;
    uniform vec2 size;
    uniform vec4 borderWidths;      // top, right, bottom, left
    uniform vec4 borderRadii;       // tl, tr, br, bl
    uniform vec4 borderTopColor;
    uniform vec4 borderRightColor;
    uniform vec4 borderBottomColor;
    uniform vec4 borderLeftColor;
    uniform vec4 backgroundColor;
    uniform bool sharpInnerEdges;   // if true, only apply AA on arc portions
    uniform bool hasBackgroundImage;
    uniform sampler2D backgroundImage;
    uniform int backgroundSize;     // 0 = cover (default), 1 = repeat, 2 = contain
    uniform vec2 imageSize;         // actual image pixel dimensions
    uniform vec2 backgroundPosition; // anchor point (0-1), e.g. (0,1)=top-left, (0.5,0.5)=center

    // Gradient support
    uniform bool hasBorderGradient;
    uniform sampler2D borderGradient;
    uniform float borderGradientAngle;
    uniform bool hasBackgroundGradient;
    uniform sampler2D backgroundGradient;
    uniform float backgroundGradientAngle;

    // Clipping support (for overflow: hidden/scroll)
    uniform bool useClipping;
    uniform vec4 clipBounds;  // minX, minY, maxX, maxY in parent local coords
    uniform vec2 parentOffset;  // child position in parent coords

    // Sample gradient based on position and angle
    vec4 sampleGradient(sampler2D grad, vec2 pos, float angle) {
        float c = cos(angle);
        float s = sin(angle);
        vec2 normPos = pos / size;
        float t = normPos.x * c + normPos.y * s;
        t = t + 0.5;
        return texture2D(grad, vec2(clamp(t, 0.0, 1.0), 0.5));
    }

    // SDF for rounded rectangle with per-corner radii
    float roundedRectSDF(vec2 p, vec2 halfSize, vec4 radii) {
        float r;
        if (p.x < 0.0 && p.y >= 0.0)      r = radii.x; // tl
        else if (p.x >= 0.0 && p.y >= 0.0) r = radii.y; // tr
        else if (p.x >= 0.0 && p.y < 0.0)  r = radii.z; // br
        else                               r = radii.w; // bl

        vec2 q = abs(p) - (halfSize - r);
        return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
    }

    // Get border color using miter line logic.
    // When one border has 0 width, the adjacent border takes over the entire corner
    vec4 getBorderColor(vec2 p, vec2 halfSize, vec2 innerHalfSize, vec2 innerOffset, vec4 innerRadii) {
        if (hasBorderGradient) {
            return sampleGradient(borderGradient, p, borderGradientAngle);
        }
        vec2 innerTL = innerOffset + vec2(-innerHalfSize.x + innerRadii.x, innerHalfSize.y - innerRadii.x);
        vec2 innerTR = innerOffset + vec2(innerHalfSize.x - innerRadii.y, innerHalfSize.y - innerRadii.y);
        vec2 innerBR = innerOffset + vec2(innerHalfSize.x - innerRadii.z, -innerHalfSize.y + innerRadii.z);
        vec2 innerBL = innerOffset + vec2(-innerHalfSize.x + innerRadii.w, -innerHalfSize.y + innerRadii.w);

        vec2 outerTL = vec2(-halfSize.x, halfSize.y);
        vec2 outerTR = vec2(halfSize.x, halfSize.y);
        vec2 outerBR = vec2(halfSize.x, -halfSize.y);
        vec2 outerBL = vec2(-halfSize.x, -halfSize.y);

        if (p.x < 0.0 && p.y >= 0.0) {
            if (borderWidths.w < 0.5) return borderTopColor;
            if (borderWidths.x < 0.5) return borderLeftColor;
            vec2 miterVec = innerTL - outerTL;
            vec2 pixelVec = p - outerTL;
            float cross = miterVec.x * pixelVec.y - miterVec.y * pixelVec.x;
            return (cross > 0.0) ? borderTopColor : borderLeftColor;
        } else if (p.x >= 0.0 && p.y >= 0.0) {
            if (borderWidths.y < 0.5) return borderTopColor;
            if (borderWidths.x < 0.5) return borderRightColor;
            vec2 miterVec = innerTR - outerTR;
            vec2 pixelVec = p - outerTR;
            float cross = miterVec.x * pixelVec.y - miterVec.y * pixelVec.x;
            return (cross > 0.0) ? borderRightColor : borderTopColor;
        } else if (p.x >= 0.0 && p.y < 0.0) {
            if (borderWidths.y < 0.5) return borderBottomColor;
            if (borderWidths.z < 0.5) return borderRightColor;
            vec2 miterVec = innerBR - outerBR;
            vec2 pixelVec = p - outerBR;
            float cross = miterVec.x * pixelVec.y - miterVec.y * pixelVec.x;
            return (cross > 0.0) ? borderBottomColor : borderRightColor;
        } else {
            if (borderWidths.w < 0.5) return borderBottomColor;
            if (borderWidths.z < 0.5) return borderLeftColor;
            vec2 miterVec = innerBL - outerBL;
            vec2 pixelVec = p - outerBL;
            float cross = miterVec.x * pixelVec.y - miterVec.y * pixelVec.x;
            return (cross > 0.0) ? borderLeftColor : borderBottomColor;
        }
    }

    // Check if point is on the arc portion of SDF (not straight edge)
    bool isOnArcPortion(vec2 p, vec2 halfSize, float r) {
        vec2 q = abs(p) - (halfSize - r);
        return q.x > 0.0 && q.y > 0.0;
    }

    float getInnerRadius(vec2 p, vec4 innerRadii) {
        if (p.x < 0.0 && p.y >= 0.0)      return innerRadii.x;
        else if (p.x >= 0.0 && p.y >= 0.0) return innerRadii.y;
        else if (p.x >= 0.0 && p.y < 0.0)  return innerRadii.z;
        else                               return innerRadii.w;
    }

    float getOuterRadius(vec2 p, vec4 outerRadii) {
        if (p.x < 0.0 && p.y >= 0.0)      return outerRadii.x;
        else if (p.x >= 0.0 && p.y >= 0.0) return outerRadii.y;
        else if (p.x >= 0.0 && p.y < 0.0)  return outerRadii.z;
        else                               return outerRadii.w;
    }

    // Get fill color from solid color, texture, or gradient
    vec4 getFillColor(vec2 uv) {
        if (hasBackgroundGradient) {
            return sampleGradient(backgroundGradient, vPos, backgroundGradientAngle);
        }
        if (!hasBackgroundImage) {
            return backgroundColor;
        }

        vec2 texUv = uv;
        float imgAspect = imageSize.x / imageSize.y;
        float boxAspect = size.x / size.y;
        vec2 anchor = backgroundPosition;

        if (backgroundSize == 0) {
            // Cover: scale to fill, crop excess
            if (imgAspect > boxAspect) {
                float scale = imgAspect / boxAspect;
                texUv.x = (uv.x - anchor.x) * scale + anchor.x;
            } else {
                float scale = boxAspect / imgAspect;
                texUv.y = (uv.y - anchor.y) * scale + anchor.y;
            }
        } else if (backgroundSize == 1) {
            // Repeat: tile at natural pixel size starting from anchor
            vec2 tileCount = size / imageSize;
            texUv = fract((uv - anchor) * tileCount);
        } else if (backgroundSize == 2) {
            // Contain: fit entire image, show background on edges
            if (imgAspect > boxAspect) {
                float scale = boxAspect / imgAspect;
                float scaledHeight = scale;
                float offset = (1.0 - scaledHeight) * anchor.y;
                texUv.y = (uv.y - offset) / scaledHeight;
                if (texUv.y < 0.0 || texUv.y > 1.0) {
                    return backgroundColor;
                }
            } else {
                float scale = imgAspect / boxAspect;
                float scaledWidth = scale;
                float offset = (1.0 - scaledWidth) * anchor.x;
                texUv.x = (uv.x - offset) / scaledWidth;
                if (texUv.x < 0.0 || texUv.x > 1.0) {
                    return backgroundColor;
                }
            }
        }

        vec4 texColor = texture2D(backgroundImage, texUv);
        texColor.a *= backgroundColor.a;
        return texColor;
    }

    void main() {
        // Clipping: discard pixels outside parent's content area
        if (useClipping) {
            vec2 worldPos = vPos + parentOffset;
            if (worldPos.x < clipBounds.x || worldPos.x > clipBounds.z ||
                worldPos.y < clipBounds.y || worldPos.y > clipBounds.w) {
                discard;
            }
        }

        vec2 halfSize = size * 0.5;

        vec2 innerSize = vec2(
            size.x - borderWidths.y - borderWidths.w,
            size.y - borderWidths.x - borderWidths.z
        );
        vec2 innerHalfSize = innerSize * 0.5;
        vec2 innerOffset = vec2(
            (borderWidths.w - borderWidths.y) * 0.5,
            (borderWidths.z - borderWidths.x) * 0.5
        );

        vec4 innerRadii = vec4(
            max(0.0, borderRadii.x - max(borderWidths.x, borderWidths.w)),
            max(0.0, borderRadii.y - max(borderWidths.x, borderWidths.y)),
            max(0.0, borderRadii.z - max(borderWidths.z, borderWidths.y)),
            max(0.0, borderRadii.w - max(borderWidths.z, borderWidths.w))
        );

        float outerDist = roundedRectSDF(vPos, halfSize, borderRadii);
        float innerDist = roundedRectSDF(vPos - innerOffset, innerHalfSize, innerRadii);

        float outerAA = fwidth(outerDist);
        float innerAA = fwidth(innerDist);

        if (outerDist > outerAA) {
            discard;
        }

        vec4 borderColor = getBorderColor(vPos, halfSize, innerHalfSize, innerOffset, innerRadii);

        float outerAlpha = 1.0;
        float outerR = getOuterRadius(vPos, borderRadii);
        if (outerR > 0.0 && outerDist > -outerAA && isOnArcPortion(vPos, halfSize, outerR)) {
            outerAlpha = 1.0 - smoothstep(-outerAA, outerAA, outerDist);
        }

        vec2 innerPos = vPos - innerOffset;
        float innerR = getInnerRadius(innerPos, innerRadii);
        bool onArc = innerR > 0.0 && isOnArcPortion(innerPos, innerHalfSize, innerR);

        bool zeroWidthBorder = false;
        if (vPos.x < innerOffset.x - innerHalfSize.x + 1.0 && borderWidths.w < 0.5) zeroWidthBorder = true; // left
        if (vPos.x > innerOffset.x + innerHalfSize.x - 1.0 && borderWidths.y < 0.5) zeroWidthBorder = true; // right
        if (vPos.y > innerOffset.y + innerHalfSize.y - 1.0 && borderWidths.x < 0.5) zeroWidthBorder = true; // top
        if (vPos.y < innerOffset.y - innerHalfSize.y + 1.0 && borderWidths.z < 0.5) zeroWidthBorder = true; // bottom

        bool applyInnerAA = onArc || !sharpInnerEdges;

        vec4 fillColor = getFillColor(vUv);

        if (applyInnerAA) {
            if (innerDist < -innerAA) {
                gl_FragColor = fillColor;
                gl_FragColor.a *= outerAlpha;
                return;
            }
            if (innerDist < 0.0) {
                float t = smoothstep(-innerAA, 0.0, innerDist);
                if (zeroWidthBorder) {
                    gl_FragColor = fillColor;
                } else {
                    vec4 fill = fillColor;
                    vec4 border = borderColor;
                    float borderAlpha = t * border.a;
                    float fillAlpha = fill.a * (1.0 - t);
                    float totalAlpha = borderAlpha + fillAlpha;
                    if (totalAlpha > 0.0) {
                        gl_FragColor.rgb = (border.rgb * borderAlpha + fill.rgb * fillAlpha) / totalAlpha;
                        gl_FragColor.a = totalAlpha;
                    } else {
                        gl_FragColor = vec4(0.0);
                    }
                }
                gl_FragColor.a *= outerAlpha;
                return;
            }
        } else {
            if (innerDist < 0.0) {
                gl_FragColor = fillColor;
                return;
            }
        }

        if (zeroWidthBorder) {
            gl_FragColor = fillColor;
            gl_FragColor.a *= outerAlpha;
            return;
        }

        borderColor.a *= outerAlpha;
        gl_FragColor = borderColor;
    }
`;

// ---- minimal clip material (ported into box-ui so it stays independent of
// imperial-text-base). Solid-color clippable shader used by scrollbars/overlays.
// Copied from the createClipMaterial in lcars MSDFText.js / text-common.js.
const clipVertexShader = `
uniform vec2 meshOffset;
varying vec2 vLocalPos;
void main() {
    vLocalPos = position.xy + meshOffset;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const clipFragmentShader = `
uniform vec3 color;
uniform float opacity;
uniform bool useClipping;
uniform vec4 clipBounds;
uniform vec2 parentOffset;
varying vec2 vLocalPos;
void main() {
    if (useClipping) {
        vec2 boxPos = vLocalPos + parentOffset;
        if (boxPos.x < clipBounds.x || boxPos.x > clipBounds.z ||
            boxPos.y < clipBounds.y || boxPos.y > clipBounds.w) {
            discard;
        }
    }
    gl_FragColor = vec4(color, opacity);
}
`;

/** Solid-color clippable material (scrollbars, overlays). Independent of text-base. */
export function createClipMaterial(colorArr, alpha = 1.0) {
    return new THREE.ShaderMaterial({
        uniforms: {
            color: { value: new THREE.Vector3(colorArr[0], colorArr[1], colorArr[2]) },
            opacity: { value: alpha },
            useClipping: { value: false },
            clipBounds: { value: new THREE.Vector4(0, 0, 0, 0) },
            parentOffset: { value: new THREE.Vector2(0, 0) },
            meshOffset: { value: new THREE.Vector2(0, 0) },
        },
        vertexShader: clipVertexShader,
        fragmentShader: clipFragmentShader,
        transparent: true,
        depthWrite: false,
        depthTest: false,
    });
}

// Build a 256x1 linear-gradient texture. Pass { stops:[{position,color:[r,g,b,a]}], angle }
// (angle in degrees). Returns { isGradient, texture, angle(radians) } for backgroundGradient /
// borderGradient props. Ported verbatim from lcars-dom/box.js createGradient.
export function createGradient({ stops, angle = 0 }) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createLinearGradient(0, 0, 256, 0);
    for (const stop of stops) {
        const [r, g, b, a] = stop.color;
        gradient.addColorStop(stop.position, `rgba(${r * 255}, ${g * 255}, ${b * 255}, ${a})`);
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 1);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;

    return { isGradient: true, texture, angle: angle * Math.PI / 180 };
}

// ---- options (text-related fields dropped vs lcars BoxOptions) ----
class BoxOptions {
    constructor(options = {}) {
        // Dimensions
        this._widthExplicit = options.width !== undefined;
        this._heightExplicit = options.height !== undefined;
        this.width = options.width ?? 0;
        this.height = options.height ?? 0;

        // Border
        this.borderWidths = [...(options.borderWidths ?? [0, 0, 0, 0])];
        this.borderRadii = [...(options.borderRadii ?? [0, 0, 0, 0])];
        this.borderColors = (options.borderColors ?? [
            [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0],
        ]).map(c => [...c]);
        this.borderOpacity = [...(options.borderOpacity ?? [0, 0, 0, 0])];
        this.borderGradient = options.borderGradient ?? null;
        this.sharpInnerEdges = options.sharpInnerEdges ?? false;

        // Background
        this.backgroundColor = [...(options.backgroundColor ?? [0, 0, 0, 0])];
        this.backgroundOpacity = options.backgroundOpacity ?? null;
        this.backgroundImage = options.backgroundImage ?? null;
        this.backgroundSize = options.backgroundSize ?? 'cover';
        this.backgroundPosition = options.backgroundPosition ?? 'center';
        this.backgroundGradient = options.backgroundGradient ?? null;

        // Layout
        this.margin = [...(options.margin ?? [0, 0, 0, 0])];
        this.padding = [...(options.padding ?? [0, 0, 0, 0])];
        this.direction = options.direction ?? options.flexDirection ?? 'row';
        this.justifyContent = options.justifyContent ?? 'start';
        this.alignItems = options.alignItems ?? 'stretch';
        this.alignSelf = options.alignSelf ?? null;
        this._growExplicit = options.grow !== undefined || options.flexGrow !== undefined;
        this.grow = options.grow ?? options.flexGrow ?? 0;
        this.shrink = options.shrink ?? options.flexShrink ?? 0;
        this.gap = options.gap ?? 0;

        // Overflow/scroll
        this.overflow = options.overflow ?? 'visible';
        this.scrollbarColor = [...(options.scrollbarColor ?? [0.6, 0.7, 1, 0.85])];   // thumb rgba
        this.scrollbarTrackColor = [...(options.scrollbarTrackColor ?? [1, 1, 1, 0.06])]; // track rgba
        this.scrollbarOpacity = options.scrollbarOpacity ?? null;   // overrides thumb alpha if set
        this.scrollbarWidth = options.scrollbarWidth ?? 10;
        this.scrollbarGap = options.scrollbarGap ?? 6;   // distance between content/text and the bar
        this.scrollbarBorderRadius = options.scrollbarBorderRadius ?? (this.scrollbarWidth / 2); // pill
        // false (default): scrollbars reserve their own track (gutter), text clips before them.
        // true: scrollbars overlay the content (no gutter, drawn on top).
        this.scrollbarOverlay = options.scrollbarOverlay ?? false;
        this.scrollOffset = { x: 0, y: 0 };
        this.contentSize = { width: 0, height: 0 };
    }
}

// Measure a non-Box child generically: honor child.getSize() if present, else
// fall back to its world-space bounding box. Returns { width, height }.
function measureForeignChild(child) {
    if (typeof child.getSize === 'function') {
        const s = child.getSize();
        if (s && typeof s.width === 'number' && typeof s.height === 'number') {
            return { width: s.width, height: s.height };
        }
    }
    // A Text mesh carries its true LAYOUT content size (full line-box stack tall, ink wide) in
    // userData — use it: it excludes selection/cursor decoration children AND is tall enough for
    // the last line to scroll fully into view (the glyph-ink bbox below is shorter and cut it off).
    if (child.userData && child.userData.contentBounds && typeof child.userData.contentBounds.width === 'number') {
        return { width: child.userData.contentBounds.width, height: child.userData.contentBounds.height };
    }
    // A child may expose a `.bounds` (e.g. text-base Text exposes textResult.bounds).
    if (child.bounds && typeof child.bounds.width === 'number') {
        return { width: child.bounds.width, height: child.bounds.height };
    }
    // Prefer the child's OWN geometry bounds over Box3.setFromObject, which would also count the
    // child's descendants — for a Text mesh that means selection/cursor/caret meshes (and the
    // caret handles stick out past the text), which would inflate the measured content size and
    // pop a spurious scrollbar once you select. Geometry bounds = just the content (glyphs).
    if (child.geometry) {
        if (!child.geometry.boundingBox) child.geometry.computeBoundingBox();
        const bb = child.geometry.boundingBox;
        if (bb) {
            const s = new THREE.Vector3();
            bb.getSize(s);
            if (s.x > 0 || s.y > 0) return { width: s.x, height: s.y };
        }
    }
    const box3 = new THREE.Box3().setFromObject(child);
    if (box3.isEmpty()) return { width: 0, height: 0 };
    const size = new THREE.Vector3();
    box3.getSize(size);
    return { width: size.x, height: size.y };
}

/**
 * Box — a layout node + render surface that hosts arbitrary child Object3Ds.
 * The mesh (rounded-rect shader) IS the Object3D you add to a scene via .mesh
 * (Box is also itself a THREE.Mesh, so `scene.add(box)` works too).
 *
 * Injection:
 *   new Box({ clickable })            // + hover/click dispatch
 *   new Box({ scrollable, overflow: 'scroll' })  // + scrollbar/scroll
 */
export class Box extends THREE.Mesh {
    constructor(options = {}) {
        const props = new BoxOptions(options);

        const hasBorderGradient = props.borderGradient?.isGradient === true;
        const hasBackgroundGradient = props.backgroundGradient?.isGradient === true;

        // Opacity overrides: backgroundOpacity wins over color alpha; per-side borderOpacity
        // wins over each border color's alpha. A background gradient ignores solid color.
        const bgAlpha = props.backgroundOpacity ?? props.backgroundColor[3] ?? 1;
        const bgColor = hasBackgroundGradient
            ? [1, 1, 1, 1]
            : [props.backgroundColor[0], props.backgroundColor[1], props.backgroundColor[2], bgAlpha];
        const borderColors = props.borderColors.map((c, i) => [c[0], c[1], c[2], props.borderOpacity[i] || (c[3] ?? 1)]);

        const backgroundPositionMap = {
            'center': [0.5, 0.5], 'top-left': [0, 1], 'top-right': [1, 1],
            'bottom-right': [1, 0], 'bottom-left': [0, 0],
        };
        const bgPosValue = backgroundPositionMap[props.backgroundPosition] ?? [0.5, 0.5];
        const backgroundSizeMap = { cover: 0, repeat: 1, contain: 2 };
        const backgroundSizeValue = backgroundSizeMap[props.backgroundSize] ?? 0;

        // CSS failsafe: limit radii to half of shortest adjacent edge
        const maxRadius = Math.min(props.width, props.height) / 2;
        const safeRadii = props.borderRadii.map(r => Math.min(r, maxRadius));

        const geometry = new THREE.PlaneGeometry(props.width, props.height);
        const material = new THREE.ShaderMaterial({
            uniforms: {
                size: { value: new THREE.Vector2(props.width, props.height) },
                borderWidths: { value: new THREE.Vector4(...props.borderWidths) },
                borderRadii: { value: new THREE.Vector4(...safeRadii) },
                borderTopColor: { value: new THREE.Vector4(...borderColors[0]) },
                borderRightColor: { value: new THREE.Vector4(...borderColors[1]) },
                borderBottomColor: { value: new THREE.Vector4(...borderColors[2]) },
                borderLeftColor: { value: new THREE.Vector4(...borderColors[3]) },
                backgroundColor: { value: new THREE.Vector4(...bgColor) },
                sharpInnerEdges: { value: props.sharpInnerEdges },
                hasBackgroundImage: { value: false },
                backgroundImage: { value: null },
                backgroundSize: { value: backgroundSizeValue },
                imageSize: { value: new THREE.Vector2(1, 1) },
                backgroundPosition: { value: new THREE.Vector2(...bgPosValue) },
                hasBorderGradient: { value: hasBorderGradient },
                borderGradient: { value: hasBorderGradient ? props.borderGradient.texture : null },
                borderGradientAngle: { value: hasBorderGradient ? props.borderGradient.angle : 0 },
                hasBackgroundGradient: { value: hasBackgroundGradient },
                backgroundGradient: { value: hasBackgroundGradient ? props.backgroundGradient.texture : null },
                backgroundGradientAngle: { value: hasBackgroundGradient ? props.backgroundGradient.angle : 0 },
                useClipping: { value: false },
                clipBounds: { value: new THREE.Vector4(0, 0, 0, 0) },
                parentOffset: { value: new THREE.Vector2(0, 0) },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            depthTest: false,
            side: THREE.DoubleSide,
        });

        super(geometry, material);

        this.isBox = true;
        this.layoutProps = props;
        this.opts = options;

        // Scrollbar meshes (created lazily by the scrollable capability or layout)
        this._scrollbarV = null;
        this._scrollbarH = null;

        // Injected capabilities. Omitting a factory keeps its module out of the graph.
        this.clickable = null;
        this.scrollable = null;
        if (options.clickable) this.clickable = options.clickable(this, options);
        if (options.scrollable) this.scrollable = options.scrollable(this, options);

        // Load a background image if one was supplied (async for path strings).
        if (props.backgroundImage) this.setBackgroundImage(props.backgroundImage);
    }

    // The Object3D to add to a scene (Box is itself a Mesh; .mesh is a convenience
    // alias so callers can use either `scene.add(box)` or `scene.add(box.mesh)`).
    get mesh() { return this; }

    // Gutter a scrollbar occupies next to the content (bar + gap). Non-zero only when a
    // bar can actually appear (overflow 'scroll' + scrollable injected) and the bars are
    // not in overlay mode. Both scroll paths and contentViewport() derive from this ONE
    // definition so the clip window, the scroll clamps and the wrap width stay in sync.
    _gutter() {
        const props = this.layoutProps;
        if (props.overflow !== 'scroll' || !this.scrollable || props.scrollbarOverlay) return 0;
        return (props.scrollbarWidth || 8) + (props.scrollbarGap ?? 6);
    }

    // The size content should be laid out to inside a scrollable box: the content box
    // minus the vertical scrollbar's gutter. Wrap text to `.w` (NOT the raw content
    // width) or the reserved gutter clips the last ~gutter px of every line. Stable —
    // it assumes the vertical bar exists, so it is safe to call before layout()/content.
    contentViewport() {
        const props = this.layoutProps;
        const cw = props.width - props.padding[1] - props.padding[3] - props.borderWidths[1] - props.borderWidths[3];
        const ch = props.height - props.padding[0] - props.padding[2] - props.borderWidths[0] - props.borderWidths[2];
        return { w: cw - this._gutter(), h: ch };
    }

    // Decide which bars are needed for a content extent (fixpoint: each bar's gutter
    // shrinks the other axis' window). Returns the viewport both scroll paths clip to.
    _scrollWindow(contentW, contentH, cw, ch) {
        const EPS = 2, g = this._gutter();
        let needsV = false, needsH = false;
        for (let i = 0; i < 2; i++) {
            needsV = contentH > (ch - (needsH ? g : 0)) + EPS;
            needsH = contentW > (cw - (needsV ? g : 0)) + EPS;
        }
        return { vw: cw - (needsV ? g : 0), vh: ch - (needsH ? g : 0) };
    }

    // Resize geometry and uniforms only — no side effects
    _resizeGeometry(newWidth, newHeight) {
        this.layoutProps.width = newWidth;
        this.layoutProps.height = newHeight;
        this.material.uniforms.size.value.set(newWidth, newHeight);
        const maxRadius = Math.min(newWidth, newHeight) / 2;
        const radii = this.layoutProps.borderRadii;
        this.material.uniforms.borderRadii.value.set(
            Math.min(radii[0], maxRadius), Math.min(radii[1], maxRadius),
            Math.min(radii[2], maxRadius), Math.min(radii[3], maxRadius)
        );
        const pos = this.geometry.attributes.position;
        const hw = newWidth / 2;
        const hh = newHeight / 2;
        pos.setXY(0, -hw, -hh);
        pos.setXY(1, hw, -hh);
        pos.setXY(2, -hw, hh);
        pos.setXY(3, hw, hh);
        pos.needsUpdate = true;
    }

    setSize(newWidth, newHeight) {
        this._resizeGeometry(newWidth, newHeight);
    }

    setBackgroundColor(r, g, b, a = 1) {
        this.material.uniforms.backgroundColor.value.set(r, g, b, a);
    }

    setBackgroundOpacity(opacity) {
        this.material.uniforms.backgroundColor.value.w = opacity;
    }

    setBackgroundImage(imageOrPath) {
        if (!imageOrPath) {
            this.material.uniforms.hasBackgroundImage.value = false;
            this.material.uniforms.backgroundImage.value = null;
            return;
        }
        const material = this.material;
        const applyTexture = (texture) => {
            material.uniforms.backgroundImage.value = texture;
            material.uniforms.hasBackgroundImage.value = true;
            const img = texture.image;
            if (img) material.uniforms.imageSize.value.set(img.width, img.height);
        };
        if (imageOrPath instanceof THREE.Texture) applyTexture(imageOrPath);
        else new THREE.TextureLoader().load(imageOrPath, applyTexture);
    }

    setBackgroundSize(mode) {
        const map = { cover: 0, repeat: 1, contain: 2 };
        this.material.uniforms.backgroundSize.value = map[mode] ?? 0;
    }

    setBackgroundPosition(position) {
        const map = {
            'top-left': [0, 1], 'center': [0.5, 0.5], 'top-right': [1, 1],
            'bottom-left': [0, 0], 'bottom-right': [1, 0],
        };
        const pos = map[position] ?? [0.5, 0.5];
        this.material.uniforms.backgroundPosition.value.set(pos[0], pos[1]);
    }

    setBackgroundGradient(gradient) {
        if (gradient?.isGradient) {
            this.material.uniforms.hasBackgroundGradient.value = true;
            this.material.uniforms.backgroundGradient.value = gradient.texture;
            this.material.uniforms.backgroundGradientAngle.value = gradient.angle;
        } else {
            this.material.uniforms.hasBackgroundGradient.value = false;
            this.material.uniforms.backgroundGradient.value = null;
        }
    }

    setBorderGradient(gradient) {
        if (gradient?.isGradient) {
            this.material.uniforms.hasBorderGradient.value = true;
            this.material.uniforms.borderGradient.value = gradient.texture;
            this.material.uniforms.borderGradientAngle.value = gradient.angle;
        } else {
            this.material.uniforms.hasBorderGradient.value = false;
            this.material.uniforms.borderGradient.value = null;
        }
    }

    setBorderOpacity(top, right, bottom, left) {
        this.material.uniforms.borderTopColor.value.w = top;
        this.material.uniforms.borderRightColor.value.w = right;
        this.material.uniforms.borderBottomColor.value.w = bottom;
        this.material.uniforms.borderLeftColor.value.w = left;
    }

    setBorderWidths(top, right, bottom, left) {
        this.material.uniforms.borderWidths.value.set(top, right, bottom, left);
        this.layoutProps.borderWidths = [top, right, bottom, left];
    }

    setBorderRadii(tl, tr, br, bl) {
        this.material.uniforms.borderRadii.value.set(tl, tr, br, bl);
        this.layoutProps.borderRadii = [tl, tr, br, bl];
    }

    setBorderColors(top, right, bottom, left) {
        this.material.uniforms.borderTopColor.value.set(...top);
        this.material.uniforms.borderRightColor.value.set(...right);
        this.material.uniforms.borderBottomColor.value.set(...bottom);
        this.material.uniforms.borderLeftColor.value.set(...left);
    }

    setSharpInnerEdges(enabled) {
        this.material.uniforms.sharpInnerEdges.value = enabled;
    }

    setPadding(top, right, bottom, left) {
        this.layoutProps.padding = [top, right, bottom, left];
        this.layout();
    }

    setMargin(top, right, bottom, left) {
        this.layoutProps.margin = [top, right, bottom, left];
    }

    setGap(gap) { this.layoutProps.gap = gap; this.layout(); }
    setDirection(direction) { this.layoutProps.direction = direction; this.layout(); }
    setJustifyContent(j) { this.layoutProps.justifyContent = j; this.layout(); }
    setAlignItems(a) { this.layoutProps.alignItems = a; this.layout(); }
    setOverflow(overflow) { this.layoutProps.overflow = overflow; this.layout(); }

    // scrollTo/scrollBy delegate to the scrollable capability if injected; a tiny
    // built-in fallback keeps a plain Box usable without the capability.
    scrollTo(x, y) {
        if (this.scrollable?.scrollTo) return this.scrollable.scrollTo(x, y);
        const dx = x - this.layoutProps.scrollOffset.x;
        const dy = y - this.layoutProps.scrollOffset.y;
        return this.scrollBy(dx, dy);
    }

    scrollBy(dx, dy) {
        if (this.scrollable?.scrollBy) return this.scrollable.scrollBy(dx, dy);
        const props = this.layoutProps;
        const contentWidth = props.width - props.padding[1] - props.padding[3] - props.borderWidths[1] - props.borderWidths[3];
        const contentHeight = props.height - props.padding[0] - props.padding[2] - props.borderWidths[0] - props.borderWidths[2];
        const maxScrollX = Math.max(0, props.contentSize.width - contentWidth);
        const maxScrollY = Math.max(0, props.contentSize.height - contentHeight);
        props.scrollOffset.x = Math.min(maxScrollX, Math.max(0, props.scrollOffset.x + dx));
        props.scrollOffset.y = Math.min(maxScrollY, Math.max(0, props.scrollOffset.y + dy));
        this.layout();
    }

    // Measure intrinsic main-axis size of this container from its children.
    // (lcars text-measurement branch dropped; foreign children measured generically.)
    measureIntrinsic(parentIsRow) {
        const props = this.layoutProps;
        const subBoxes = this.children.filter(c => c.isBox && !c.userData?.layoutIgnore);
        const foreign = this.children.filter(c => !c.isBox && c._isScrollbar !== true && c.visible !== false);
        if (subBoxes.length === 0 && foreign.length === 0) return 0;

        const childIsRow = props.direction === 'row';
        const needSum = parentIsRow === childIsRow;
        const needWidth = parentIsRow;

        let result = 0;
        const all = [...subBoxes, ...foreign];
        for (const sub of all) {
            let size;
            let margin = 0;
            if (sub.isBox) {
                const sp = sub.layoutProps;
                size = needWidth ? sp.width : sp.height;
                const explicit = needWidth ? sp._widthExplicit : sp._heightExplicit;
                if (size === 0 && !explicit && sub.children?.some(c => c.isBox || !c.isBox)) {
                    size = sub.measureIntrinsic(parentIsRow);
                }
                margin = needWidth ? sp.margin[1] + sp.margin[3] : sp.margin[0] + sp.margin[2];
            } else {
                const m = measureForeignChild(sub);
                size = needWidth ? m.width : m.height;
            }
            if (needSum) result += size + margin;
            else result = Math.max(result, size + margin);
        }
        const count = all.length;
        if (needSum) result += Math.max(0, count - 1) * props.gap;
        if (needWidth) result += props.padding[1] + props.padding[3] + props.borderWidths[1] + props.borderWidths[3];
        else result += props.padding[0] + props.padding[2] + props.borderWidths[0] + props.borderWidths[2];
        return result;
    }

    // Layout calculation - positions all children (Box children flex; foreign
    // children are placed/measured generically).
    layout() {
        const props = this.layoutProps;
        // userData.layoutIgnore: a box child positioned MANUALLY (e.g. the XR on-screen
        // keyboard parked next to a field) — flex must neither move nor measure it.
        const children = this.children.filter(c => c.isBox && !c.userData?.layoutIgnore);
        const foreignChildren = this.children.filter(c => !c.isBox && !c._isScrollbar);

        // No Box children: auto-size from foreign children if no explicit size.
        if (children.length === 0) {
            if (foreignChildren.length > 0 && (!props._widthExplicit || !props._heightExplicit)) {
                let maxW = 0, maxH = 0;
                for (const fc of foreignChildren) {
                    const m = measureForeignChild(fc);
                    maxW = Math.max(maxW, m.width);
                    maxH = Math.max(maxH, m.height);
                }
                const padW = props.padding[1] + props.padding[3] + props.borderWidths[1] + props.borderWidths[3];
                const padH = props.padding[0] + props.padding[2] + props.borderWidths[0] + props.borderWidths[2];
                // _crossImposed: the parent's alignItems:'stretch' just sized this axis
                // (transient, set around child.layout() in the stretch branch below).
                // An auto-sized text box is a flex LEAF: its intrinsic size must not
                // collapse a parent-stretched axis back to content size — that made
                // empty form inputs render ~12px wide instead of the dialog width.
                this.setSize(
                    (props._widthExplicit || props._crossImposed === 'width') ? props.width : maxW + padW,
                    (props._heightExplicit || props._crossImposed === 'height') ? props.height : maxH + padH,
                );
                // Re-place the owned text (box-text) for the (possibly new) size, so a
                // stretched input's text aligns to the full width, not the old hug-width.
                this._relayoutText?.();
            }
            // A fixed-size scrollable box whose content is a foreign child (e.g. a Text mesh):
            // measure it, scroll it, clip it, and drive the scrollbar. (The Box-child path below
            // handles the flex case; this covers "scroll a big block of text with a scrollbar".)
            if (props.overflow !== 'visible' && foreignChildren.length > 0) {
                const cx = -props.width / 2 + props.padding[3] + props.borderWidths[3];
                const cy = props.height / 2 - props.padding[0] - props.borderWidths[0];
                const cw = props.width - props.padding[1] - props.padding[3] - props.borderWidths[1] - props.borderWidths[3];
                const ch = props.height - props.padding[0] - props.padding[2] - props.borderWidths[0] - props.borderWidths[2];
                this._scrollClipForeign(foreignChildren, cx, cy, cw, ch);
            }
            this.updateRenderOrder(this.renderOrder || 0);
            return;
        }

        const isRow = props.direction === 'row';

        // Auto-size: if main-axis size is 0 and not explicit, calculate from children
        const noExplicitMain = isRow ? !props._widthExplicit : !props._heightExplicit;
        if (noExplicitMain) {
            const mainVal = isRow ? props.width : props.height;
            if (mainVal === 0 && children.length > 0) {
                const intrinsic = this.measureIntrinsic(isRow);
                if (intrinsic > 0) {
                    if (isRow) this.setSize(intrinsic, props.height);
                    else this.setSize(props.width, intrinsic);
                }
            }
        }

        // Content area (inside padding and borders)
        const contentX = -props.width / 2 + props.padding[3] + props.borderWidths[3];
        const contentY = props.height / 2 - props.padding[0] - props.borderWidths[0];
        const contentWidth = props.width - props.padding[1] - props.padding[3] - props.borderWidths[1] - props.borderWidths[3];
        const contentHeight = props.height - props.padding[0] - props.padding[2] - props.borderWidths[0] - props.borderWidths[2];

        let totalFixed = 0;
        let totalGrow = 0;
        let totalShrink = 0;
        let maxCrossExtent = 0;   // widest explicit cross size (for the H/V bar decision)
        const childBaseSizes = [];
        for (const child of children) {
            const cp = child.layoutProps;
            let baseSize = isRow ? cp.width : cp.height;
            const marginSize = isRow ? cp.margin[1] + cp.margin[3] : cp.margin[0] + cp.margin[2];

            // Container-based sizing: child with no explicit main size but has
            // children → measure intrinsic size from its children.
            if (baseSize === 0) {
                const noExplicitMainC = isRow ? !cp._widthExplicit : !cp._heightExplicit;
                if (noExplicitMainC && child.measureIntrinsic) {
                    baseSize = child.measureIntrinsic(isRow);
                }
            }

            // Implicit grow: 0-size children that didn't set grow behave as grow:1
            const effectiveGrow = (baseSize === 0 && cp.grow === 0 && !cp._growExplicit) ? 1 : cp.grow;
            childBaseSizes.push({ baseSize, marginSize, effectiveGrow });
            totalFixed += baseSize + marginSize;
            totalGrow += effectiveGrow;
            totalShrink += cp.shrink * baseSize;

            const crossBase = isRow ? cp.height : cp.width;
            const crossMargins = isRow ? cp.margin[0] + cp.margin[2] : cp.margin[3] + cp.margin[1];
            maxCrossExtent = Math.max(maxCrossExtent, crossBase + crossMargins);
        }
        totalFixed += Math.max(0, children.length - 1) * props.gap;

        // Scrollbar gutter: a scrolling box lays its children out in the VIEWPORT (content
        // minus bar gutters), so the bars never overlay the children and stretch children
        // stop short of the bar. overflow:'visible' (or no scrollable) → viewport = content.
        let vw = contentWidth, vh = contentHeight;
        if (props.overflow !== 'visible') {
            const w = this._scrollWindow(
                isRow ? totalFixed : maxCrossExtent,
                isRow ? maxCrossExtent : totalFixed,
                contentWidth, contentHeight);
            vw = w.vw; vh = w.vh;
            props._scrollViewport = { w: vw, h: vh };
        }
        const mainSize = isRow ? vw : vh;
        const crossSize = isRow ? vh : vw;

        const freeSpace = mainSize - totalFixed;
        const needsGrow = freeSpace > 0 && totalGrow > 0;
        const needsShrink = freeSpace < 0 && totalShrink > 0;

        let mainPos = 0;
        const distributedSpace = (needsGrow || needsShrink) ? 0 : Math.max(0, freeSpace);

        if (props.justifyContent === 'center') {
            mainPos = distributedSpace / 2;
        } else if (props.justifyContent === 'end') {
            mainPos = distributedSpace;
        }

        const spaceBetween = props.justifyContent === 'space-between' && children.length > 1
            ? distributedSpace / (children.length - 1) : 0;
        const spaceAround = props.justifyContent === 'space-around'
            ? distributedSpace / children.length : 0;

        if (props.justifyContent === 'space-around') {
            mainPos = spaceAround / 2;
        }

        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            const cp = child.layoutProps;
            const { baseSize, effectiveGrow } = childBaseSizes[i];

            let childMainSize = baseSize;
            if (needsGrow && effectiveGrow > 0) {
                childMainSize += (freeSpace * effectiveGrow) / totalGrow;
            } else if (needsShrink && cp.shrink > 0) {
                const shrinkRatio = (cp.shrink * baseSize) / totalShrink;
                childMainSize += freeSpace * shrinkRatio;
            }
            childMainSize = Math.max(0, childMainSize);

            const currentMainSize = isRow ? cp.width : cp.height;
            if (childMainSize !== currentMainSize) {
                if (isRow) child.setSize(childMainSize, cp.height);
                else child.setSize(cp.width, childMainSize);
            }

            let childCrossSize = isRow ? cp.height : cp.width;
            const marginMain = isRow ? [cp.margin[3], cp.margin[1]] : [cp.margin[0], cp.margin[2]];
            const marginCross = isRow ? [cp.margin[0], cp.margin[2]] : [cp.margin[3], cp.margin[1]];

            mainPos += marginMain[0];
            const childMainPos = mainPos + childMainSize / 2;
            mainPos += childMainSize + marginMain[1];

            const align = cp.alignSelf ?? props.alignItems;
            let crossPos;
            let actualCrossSize = childCrossSize;
            if (align === 'center') {
                crossPos = crossSize / 2;
            } else if (align === 'end') {
                crossPos = crossSize - marginCross[1] - childCrossSize / 2;
            } else if (align === 'stretch') {
                const hasExplicitCross = isRow ? cp._heightExplicit : cp._widthExplicit;
                if (!hasExplicitCross) {
                    actualCrossSize = crossSize - marginCross[0] - marginCross[1];
                    if (isRow) child.setSize(cp.width, actualCrossSize);
                    else child.setSize(actualCrossSize, cp.height);
                    // Transient marker for the child's own layout(): this axis was
                    // IMPOSED by stretch — its leaf auto-size must not undo it.
                    cp._crossImposed = isRow ? 'height' : 'width';
                }
                crossPos = marginCross[0] + actualCrossSize / 2;
            } else { // start
                crossPos = marginCross[0] + childCrossSize / 2;
            }

            if (isRow) {
                child.position.x = contentX + childMainPos;
                child.position.y = contentY - crossPos;
            } else {
                child.position.x = contentX + crossPos;
                child.position.y = contentY - childMainPos;
            }
            child.position.z = 0;

            if (i < children.length - 1) {
                mainPos += props.gap + spaceBetween;
                if (props.justifyContent === 'space-around') mainPos += spaceAround;
            }

            child.layout();
            cp._crossImposed = null;   // transient — valid only for the layout() above
        }

        // Store content size for scroll calculations. The cross extent is the widest
        // child (explicit sizes may exceed the viewport), never less than the viewport.
        props.contentSize.width = isRow ? mainPos : Math.max(crossSize, maxCrossExtent);
        props.contentSize.height = isRow ? Math.max(crossSize, maxCrossExtent) : mainPos;

        // Handle overflow/scrolling. If the scrollable capability is injected it owns
        // the scrollbar meshes + applies the offset; otherwise a built-in path here
        // applies the scroll offset and clip uniforms so plain overflow:'hidden' works.
        if (props.overflow !== 'visible') {
            // Clamp the offset to the viewport (content may have shrunk since the last scroll).
            props.scrollOffset.x = Math.min(Math.max(0, props.contentSize.width - vw), Math.max(0, props.scrollOffset.x));
            props.scrollOffset.y = Math.min(Math.max(0, props.contentSize.height - vh), Math.max(0, props.scrollOffset.y));
            if (props.scrollOffset.x !== 0 || props.scrollOffset.y !== 0) {
                for (const child of children) {
                    child.position.x -= props.scrollOffset.x;
                    child.position.y += props.scrollOffset.y;
                }
            }
            // Clip children to the viewport — including their DESCENDANTS (a child box's
            // own text/selection/nested meshes), or scrolled-out content inside a child
            // stays visible outside the window.
            const clip = { minX: contentX, minY: contentY - vh, maxX: contentX + vw, maxY: contentY };
            for (const child of children) {
                if (child.material && child.material.uniforms && child.material.uniforms.useClipping) {
                    child.material.uniforms.useClipping.value = true;
                    child.material.uniforms.clipBounds.value.set(clip.minX, clip.minY, clip.maxX, clip.maxY);
                    child.material.uniforms.parentOffset.value.set(child.position.x, child.position.y);
                }
                this._clipDescendants(child, clip, child.position.x, child.position.y, 0, 0);
            }
            // Let the scrollable capability render/update its scrollbars (bars sit at the
            // outer content edges, i.e. inside the reserved gutters).
            if (this.scrollable?.updateScrollbars) {
                this.scrollable.updateScrollbars(contentX, contentY, contentWidth, contentHeight);
            }
        }

        this.updateRenderOrder(this.renderOrder || 0);
    }

    // Scroll + clip foreign (non-Box) children to the content area and size the scrollbar from
    // their extent. Used when a fixed-size scrollable box's content is a foreign object such as a
    // Text mesh. Each child's un-scrolled position is captured once (_sbX/_sbY) so re-layout
    // repositions from the base instead of drifting. Coordinates match the Box-child scroll path:
    // children shift by (-scrollOffset.x, +scrollOffset.y).
    _scrollClipForeign(foreignChildren, cx, cy, cw, ch) {
        const props = this.layoutProps;
        let maxW = 0, maxH = 0;
        for (const fc of foreignChildren) {
            const m = measureForeignChild(fc);
            maxW = Math.max(maxW, m.width);
            maxH = Math.max(maxH, m.height);
        }
        props.contentSize.width = maxW;
        props.contentSize.height = maxH;
        // scrollbarOverlay: false (default) = each bar gets its OWN TRACK — reserve a gutter
        // (scrollbarWidth + GAP, see _gutter) so the text clips before the bar. true =
        // OVERLAY — no gutter, text uses the full area and the bar draws on top of it.
        // NOTE for wrapped text: wrap it to box.contentViewport().w, not the raw content
        // width — the gutter is INSIDE the content box, so a full-width wrap gets clipped.
        const { vw, vh } = this._scrollWindow(maxW, maxH, cw, ch);
        // Publish the reduced viewport so scrollable.scrollBy clamps to it (not the full box) —
        // else scroll stops short of the gutter and the last line/column can't reach view.
        props._scrollViewport = { w: vw, h: vh };
        props.scrollOffset.x = Math.min(Math.max(0, maxW - vw), Math.max(0, props.scrollOffset.x));
        props.scrollOffset.y = Math.min(Math.max(0, maxH - vh), Math.max(0, props.scrollOffset.y));
        const clip = { minX: cx, minY: cy - vh, maxX: cx + vw, maxY: cy };
        for (const fc of foreignChildren) {
            if (fc.userData._sbX === undefined) { fc.userData._sbX = fc.position.x; fc.userData._sbY = fc.position.y; }
            fc.position.x = fc.userData._sbX - props.scrollOffset.x;
            fc.position.y = fc.userData._sbY + props.scrollOffset.y;
            const u = fc.material?.uniforms;
            if (u && u.useClipping) {
                u.useClipping.value = true;
                u.clipBounds.value.set(clip.minX, clip.minY, clip.maxX, clip.maxY);
                u.parentOffset.value.set(fc.position.x, fc.position.y);
            }
            // Also clip the foreign child's OWN descendants — e.g. a Text mesh's selection /
            // cursor / caret meshes — or a scrolled-away selection stays visible outside the box.
            // Their shaders carry a meshOffset (geometry sits at mesh.position), so pass each
            // one's accumulated offset within the foreign child; parentOffset stays the child's.
            this._clipDescendants(fc, clip, fc.position.x, fc.position.y, 0, 0);
        }
        // Bars still render along the full content edges (i.e. inside the reserved gutter).
        if (this.scrollable?.updateScrollbars) this.scrollable.updateScrollbars(cx, cy, cw, ch);
    }

    // Clip a subtree to `clip` (box-local bounds). Two shader families exist:
    //  - geometry-absolute meshes (Box, Text): vertices carry their own coords, the shader
    //    adds `parentOffset` — set it to the mesh's ACCUMULATED box-local position; the
    //    mesh then becomes the coordinate ROOT for its own descendants.
    //  - clip-material meshes (cursor/selection/carets — have a `meshOffset` uniform):
    //    geometry sits at the mesh origin; `meshOffset` = accumulated offset within the
    //    current root, `parentOffset` = the root's box-local position.
    // baseX/baseY = the current root's box-local position; accX/accY = offset within it.
    // inCaret: inside a selection-caret subtree — its clip window is EXTENDED below the
    // content (handles hang under their line; the box's last line must stay grabbable).
    _clipDescendants(node, clip, baseX, baseY, accX, accY, inCaret = false) {
        const CARET_CLIP_PAD = 40;
        for (const child of node.children) {
            if (child._isScrollbar) continue;
            // A child Box that clips manages its own subtree — don't overwrite its window.
            if (child.isBox && child.layoutProps?.overflow !== 'visible') continue;
            const caret = inCaret || !!child.userData?.isSelectionCaret;
            const ax = accX + child.position.x, ay = accY + child.position.y;
            let nBaseX = baseX, nBaseY = baseY, nAccX = ax, nAccY = ay;
            const cu = child.material?.uniforms;
            if (cu && cu.useClipping !== undefined) {
                cu.useClipping.value = true;
                cu.clipBounds.value.set(clip.minX, caret ? clip.minY - CARET_CLIP_PAD : clip.minY, clip.maxX, clip.maxY);
                if (cu.meshOffset) {
                    if (cu.parentOffset) cu.parentOffset.value.set(baseX, baseY);
                    cu.meshOffset.value.set(ax, ay);
                } else if (cu.parentOffset) {
                    cu.parentOffset.value.set(baseX + ax, baseY + ay);
                    nBaseX = baseX + ax; nBaseY = baseY + ay; nAccX = 0; nAccY = 0;
                }
            }
            this._clipDescendants(child, clip, nBaseX, nBaseY, nAccX, nAccY, caret);
        }
    }

    updateRenderOrder(baseOrder) {
        this.renderOrder = baseOrder;
        let order = baseOrder + 1;

        // Foreign children (e.g. a Text label) render on top of this box.
        const foreign = this.children.filter(c => !c.isBox && !c._isScrollbar);
        for (const fc of foreign) {
            fc.renderOrder = order++;
            // children (text) strictly between their box and the NEXT sibling: with the same
            // number as the next box, three fell back to a depth sort — in VR the per-eye
            // depths tie/flip frame to frame and texts blinked (2026-10-10)
            for (const g of fc.children || []) g.renderOrder = order - 0.5;
        }

        const children = this.children.filter(c => c.isBox && !c.userData?.layoutIgnore);
        for (const child of children) {
            child.updateRenderOrder(order);
            order = child._maxRenderOrder + 1;
        }

        // Tracks first, thumbs after (thumb draws over its track); both above all children.
        for (const bar of [this._scrollbarTrackV, this._scrollbarTrackH, this._scrollbarV, this._scrollbarH]) {
            if (bar) bar.renderOrder = order++;
        }

        this._maxRenderOrder = order;
    }

    dispose() {
        if (this.clickable?.dispose) this.clickable.dispose();
        if (this.scrollable?.dispose) this.scrollable.dispose();
        // Text capability (box-text): unregisters the interaction target + disposes the text mesh.
        if (this._textHandle?.dispose) this._textHandle.dispose();
        for (const child of [...this.children]) {
            if (child.isBox && child.dispose) child.dispose();
            else {
                child.geometry?.dispose?.();
                child.material?.dispose?.();
            }
            this.remove(child);
        }
        this.geometry?.dispose?.();
        this.material?.dispose?.();
        if (this.parent) this.parent.remove(this);
    }
}

export default Box;
